import cn from "classnames"
import { useEffect, useState } from "react"

import { defaultCoverFor } from "@utils/localCoverImage"

interface Props {
  src: string
  isInvitation: boolean
  seed?: string | number
  // INBOX-REBUILD 2026-07-03: "cover" = Ale's full-width grid-card hero image (.img, 168px tall,
  // 8px margin, radius 8). "thumb" (default) = the legacy 72x72 thumbnail. "lrow-thumb" = Ale's
  // .lrow .thumb (56x56, radius 12, flex-none, FIRST child of the list row). Same component,
  // same fallback/error-recovery logic in every variant.
  variant?: "thumb" | "cover" | "lrow-thumb"
}

// A TeeUp must never render without a cover. Pick a stable default from the bundled set so the same
// TeeUp always gets the same image (no flicker, no network dependency — source.unsplash.com is dead).
// CREATE-COVER 2026-07-04: defaultCoverFor now lives in @utils/localCoverImage (shared with the
// create form's seed-cover logic) instead of being defined locally here.
const TeeUpPhoto = ({ src, isInvitation, seed, variant = "thumb" }: Props) => {
  const fallback = defaultCoverFor(seed)
  // Start with the real cover if present; fall back to the bundled default on empty OR load error
  // (covers nulled placeholders, bare-created teeups, and dead 404 URLs like the old "catch up").
  const [imgSrc, setImgSrc] = useState<string>(src || fallback)

  // BUG-024: the cover often arrives AFTER the first render (the inbox card mounts before the
  // teeup's photo is populated). useState only seeds on mount, so the card stayed on the fallback
  // even once the real cover was known. Resync whenever the incoming src changes.
  useEffect(() => {
    setImgSrc(src || fallback)
  }, [src, fallback])

  if (variant === "lrow-thumb") {
    return (
      <div className="size-[56px] flex-none rounded-xl border border-neutral-200 overflow-hidden">
        <img
          src={imgSrc}
          alt="teeup image"
          className="size-[56px] object-cover"
          onError={() => {
            if (imgSrc !== fallback) setImgSrc(fallback)
          }}
        />
      </div>
    )
  }

  if (variant === "cover") {
    return (
      // Ale's .tcard .img is 150px tall (verbatim from the compiled prototype). Was 168px.
      <div className="h-[150px] w-full m-0 rounded-lg overflow-hidden">
        <img
          src={imgSrc}
          alt="teeup image"
          className="w-full h-full object-cover"
          onError={() => {
            if (imgSrc !== fallback) setImgSrc(fallback)
          }}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        "size-[72px] rounded border border-neutral-200 overflow-hidden",
        {
          "-translate-y-1/3": !isInvitation,
        }
      )}>
      <img
        src={imgSrc}
        alt="teeup image"
        className="size-[72px] object-cover"
        onError={() => {
          if (imgSrc !== fallback) setImgSrc(fallback)
        }}
      />
    </div>
  )
}

export default TeeUpPhoto
